import { UpdateClientDto } from '@/schemas/updateClientSchema.ts';
import {
  BrandWikiSuccessResponse,
  GeneratePostIdeasRequest,
  GeneratePostIdeasSuccessResponse,
  GeneratePostRequest,
  GeneratePostSuccessResponse,
  PostIdeasSuccessResponse,
} from '@/types';
import { SiteStatusResponse } from '@/types/chat';
import {
  GetProjectsResponse,
  ProjectWizardResponse,
  TProjectStatus,
  UpdateProjectsResponse,
} from '@/types/projects.ts';
import { uuidToColor } from '@/lib/utils/common.ts';

import axiosInstance from './axiosInstance';
import { TaskResults } from './wizard';

export const getBrandWiki = (
  projectId: string
): Promise<BrandWikiSuccessResponse> =>
  axiosInstance.get(`/projects/${projectId}/brand-wiki`);

export const putBrandWikiSection = ({
  projectId,
  sectionId,
  title,
  content,
}: {
  projectId: string;
  sectionId: string;
  title: string;
  content: string;
}): Promise<BrandWikiSuccessResponse> =>
  axiosInstance.put(`/projects/${projectId}/brand-wiki/${sectionId}`, {
    title,
    content,
  });

export const getPostIdeas = (
  projectId: string
): Promise<PostIdeasSuccessResponse> => {
  return axiosInstance.get(`/projects/${projectId}/ideas`);
};

export const generatePostIdeas = async ({
  projectId,
  data,
}: GeneratePostIdeasRequest): Promise<GeneratePostIdeasSuccessResponse> => {
  // await new Promise((resolve) => setTimeout(resolve, 1000));
  return axiosInstance.post(`/projects/${projectId}/generate-ideas`, {
    num_ideas: data.num_ideas,
    temperature: data.temperature,
  });
};

export const generatePost = ({
  projectId,
  data,
}: GeneratePostRequest): Promise<GeneratePostSuccessResponse> =>
  axiosInstance.post(`/projects/${projectId}/generate-post`, {
    idea: data.idea,
    temperature: data.temperature,
  });

export const ResetProject = (projectId: string): Promise<boolean> =>
  axiosInstance.post(`/projects/${projectId}/delete-all-data`);

export const getProjects = (): Promise<GetProjectsResponse> => {
  return axiosInstance.get(`/projects/`).then((data) => {
    if (data?.data) {
      const dataData = (data as unknown as GetProjectsResponse).data;
      data.data = dataData.map((d) => ({
        ...d,
        status: TProjectStatus.ACTIVE,
        marker: uuidToColor(d.id),
        totalPosts: 0,
      }));
    }
    return data as unknown as GetProjectsResponse;
  });
};

export const updateProject = (
  clientId: string,
  body: UpdateClientDto
): Promise<UpdateProjectsResponse> => {
  return axiosInstance.patch(`/projects/${clientId}`, body);
};

export const createProject = (
  body: UpdateClientDto
): Promise<UpdateProjectsResponse> => {
  return axiosInstance.post(`/projects/`, body);
};

export const deleteProject = (
  clientId: string
): Promise<UpdateProjectsResponse> => {
  return axiosInstance.delete(`/projects/${clientId}`);
};

export const projectLandingWizardGet = (
  projectId: string,
  stateId: string
): Promise<ProjectWizardResponse> => {
  return axiosInstance.get(`/projects/${projectId}/wizard?state_id=${stateId}`);
};

export const projectLandingWizardStart = (
  projectId: string
): Promise<ProjectWizardResponse> => {
  return axiosInstance.get(`/projects/${projectId}/wizard/start`);
};

export const projectLandingWizardPost = (
  projectId: string,
  state_id: string,
  answer: string
): Promise<ProjectWizardResponse> => {
  return axiosInstance.post(`/projects/${projectId}/wizard`, {
    state_id,
    answer,
  });
};

export const wizardGetNextQuestion = (
  projectId: string,
  stateId: string
): Promise<ProjectWizardResponse> => {
  return axiosInstance.get(`/projects/${projectId}/wizard&state_id=${stateId}`);
};

export const addWebsite = (
  projectId: string,
  stateId: string,
  url: string
): Promise<SiteStatusResponse> => {
  return axiosInstance.post(`/projects/${projectId}/wizard/add-site`, {
    state_id: stateId,
    answer: url,
  });
};

export const checkSiteStatus = (
  projectId: string,
  stateId: string
): Promise<SiteStatusResponse> => {
  return axiosInstance.post(`/projects/${projectId}/wizard/site-status`, {
    state_id: stateId,
  });
};

export const checkBrandStrategyStatus = (
  projectId: string,
  taskIds: string
): Promise<{ data: TaskResults }> => {
  return axiosInstance.get(
    `/projects/${projectId}/wizard/task-status?${taskIds}`
  );
};
